import {
  MODEL_GAITS,
  MODEL_IDLE,
  MODEL_MOTION,
  circleArcSeconds,
  circleAt,
  clipTimeScale,
  faceAt,
  faceSeconds,
  modelFront,
  modelStopDistance,
  modelStopPoint,
  shortestTurn,
  showTurnAt,
  showTurnSeconds,
  turnAt,
  turnSeconds,
  turnTowards,
  walkAt,
  walkSeconds,
  walkSpeed,
  walkingGait,
  type ModelAnimation,
  type ModelCircle,
  type ModelFacts,
  type ModelGait,
  type StepModel,
} from '@vrlearning/lesson-schema';

/**
 * One model's part in a step, as the headset plays it (ADR-043; spec "Engine
 * behaviour"): pure, no three.js, so the timing is tested here and the
 * runtime (runtime.ts) only copies the pose onto the model and its mixer.
 *
 * An entry runs in order, as lesson-schema's replay counts it:
 *
 * 1. `walkTo: [x, z]` -- a turn on the spot at 90°/s with the gait's clip at
 *    half speed, then straight there with the 0.8 s ramp and the 5 m/s² brake
 *    (`walkAt`), the clip's speed following the ground's (`clipTimeScale`) so
 *    the feet never slide.
 * 2. `walkTo: "pupil"` (Spannend only) -- the same turn towards the eyes, then
 *    a run at them frame by frame (`walkSpeed`), towards where the clamp
 *    stops its front (`modelStopPoint`, 2.5 m from the eyes as they are now,
 *    whatever the file says). It never moves on while the clamp is reached,
 *    and a model already that near neither walks nor turns: turning it would
 *    swing a bite past the clamp (A1's note).
 * 2b. `turn` ("Wat wil je maken?" plan 4) -- turned where it stands to a heading, eased over
 *    `showTurnSeconds` (a quarter turn in 2 s), whatever it plays meanwhile (the idle after a gait): a
 *    static exhibit turns too. Never beside a walk, a face or a circle (the validator refuses those).
 * 3. `face: "pupil"` -- a smoothstep turn of `faceSeconds` to the eyes, the
 *    idle playing; skipped when facing would bring its front past the clamp.
 * 4. `play` -- looping (the library's say, or `loop`), or once for its
 *    seconds and then `then` (default `staan`); without a play, a walk ends in
 *    `then` and `then` alone is played.
 *
 * 5. `circle` (ADR-054) -- the same turn on the spot to the ring's tangent, then round the ring as
 *    lesson-schema's `circleAt` has it (up to speed over 0.8 s, facing along it, the loop at
 *    `clipTimeScale`); a finite circle brakes to its end and holds `then` (default `staan`), a loop goes
 *    round for good and counts as done (settled) once it does.
 *
 * Positions are [x, z] on the ground, turns degrees in the rig's yaw
 * convention (0 faces −z, positive turns left), as everywhere in lesson-schema.
 */

export type Ground = readonly [number, number];

/** Where a model is and what plays on it this frame. */
export type PerformancePose = {
  readonly pos: Ground;
  readonly turn: number;
  readonly animation: ModelAnimation;
  /** The clip's speed: the gait's 0.5 turning, `clipTimeScale` walking, 1 otherwise. */
  readonly timeScale: number;
  /** Whether the clip loops; a once-only play (and a once-only clip held) stops on its last frame. */
  readonly loop: boolean;
};

export type PerformanceInput = {
  readonly facts: Pick<ModelFacts, 'box' | 'animations'>;
  /** `modelScale`: its `size` over its real size. */
  readonly scale: number;
  /** Where the step finds it. */
  readonly start: PerformancePose;
  /** What the step asks of it: the step's `models` entry, or its `spannend` part's. */
  readonly entry: StepModel;
  /** Where the pupil's eyes are when the step starts, [x, z] in the world. */
  readonly eye: Ground;
  /**
   * A walk to the pupil as lesson-schema's `spannendWalk` plans it from these
   * eyes -- where the clamp stops it (`to` is where it stands when it is
   * already that near), its heading, the clips the clamp counts -- so the
   * headset clamps where the validator judged. Without it, worked out here
   * with the same `modelStopPoint`.
   */
  readonly charge?: {
    readonly to: Ground;
    readonly heading: number;
    readonly clips: readonly ModelAnimation[];
  };
  /**
   * The ring an entry with `circle` goes round (ADR-054), as lesson-schema's replay makes it where the
   * step finds the model (`ModelStepReplay.circle`): the runtime passes it, so the headset follows the
   * ring the validator judged. Without it, or without its loop's speed, the entry only plays.
   */
  readonly circle?: ModelCircle | undefined;
};

type Phase =
  | { kind: 'turn'; from: number; to: number; seconds: number; gait: ModelGait }
  | { kind: 'walk'; from: Ground; to: Ground; heading: number; gait: ModelGait; speed: number }
  | { kind: 'charge'; gait: ModelGait; speed: number; clips: readonly ModelAnimation[] }
  | { kind: 'orbit'; circle: ModelCircle; speed: number }
  | { kind: 'show'; from: number; to: number; seconds: number }
  | { kind: 'face' }
  | { kind: 'play'; animation: ModelAnimation; seconds: number | undefined; then: ModelAnimation }
  | { kind: 'hold'; animation: ModelAnimation; loop: boolean };

const isGait = (value: unknown): value is ModelGait =>
  (MODEL_GAITS as readonly unknown[]).includes(value);

const distance = (a: Ground, b: Ground): number => Math.hypot(b[0] - a[0], b[1] - a[1]);

/** How long a cross-fade into `to` takes: 0.25 s into a bite, 0.8 s back to the idle, else 0.35 s. */
export function fadeFor(to: ModelAnimation): number {
  if (to === 'bijten') return MODEL_MOTION.fadeBite;
  if (to === MODEL_IDLE) return MODEL_MOTION.fadeIdle;
  return MODEL_MOTION.fade;
}

/** Below this many metres from the clamp a walk to the pupil has arrived. */
const ARRIVED = 0.02;
/** A face or turn shorter than this is no turn at all. */
const NO_TURN = 0.5;

export class ModelPerformance {
  readonly #facts: PerformanceInput['facts'];
  readonly #scale: number;
  readonly #phases: Phase[] = [];
  #at = 0;
  /** Seconds into the phase. */
  #t = 0;
  #pos: [number, number];
  #turn: number;
  #animation: ModelAnimation;
  #timeScale = 1;
  #loop: boolean;
  /** The face's start and end, worked out when it starts (the eyes may have moved). */
  #face: { from: number; to: number; seconds: number } | undefined;
  /** The eyes as last told. */
  #eye: Ground;
  /** What the entry plays after its walk: what a face must keep clear of the clamp. */
  #after: ModelAnimation[] = [];
  /** The ring it goes round for good, when its entry is a loop. */
  #looping: ModelCircle | undefined;

  constructor(input: PerformanceInput) {
    this.#facts = input.facts;
    this.#scale = input.scale;
    this.#pos = [input.start.pos[0], input.start.pos[1]];
    this.#turn = input.start.turn;
    this.#animation = input.start.animation;
    this.#timeScale = input.start.timeScale;
    this.#loop = input.start.loop;
    this.#eye = input.eye;
    this.#plan(input);
    this.#enter();
  }

  /** Every phase played: it holds its last pose from now on -- or goes round its ring for good. */
  get done(): boolean {
    const phase = this.#phases[this.#at];
    return (
      phase === undefined ||
      phase.kind === 'hold' ||
      (phase.kind === 'orbit' && phase.circle.rounds === 'loop')
    );
  }

  /** The ring it goes round for good (a loop, ADR-054): the runtime carries it into later steps. */
  get looping(): ModelCircle | undefined {
    return this.#looping;
  }

  get pose(): PerformancePose {
    return {
      pos: [this.#pos[0], this.#pos[1]],
      turn: this.#turn,
      animation: this.#animation,
      timeScale: this.#timeScale,
      loop: this.#loop,
    };
  }

  /** Seconds into its ring's arc while it goes round it; undefined otherwise (turning in, say). */
  get orbitTime(): number | undefined {
    return this.#phases[this.#at]?.kind === 'orbit' ? this.#t : undefined;
  }

  /** The ring its entry goes round (a loop or a number of rounds), if it goes round at all. */
  get ring(): ModelCircle | undefined {
    for (const phase of this.#phases) if (phase.kind === 'orbit') return phase.circle;
    return undefined;
  }

  /**
   * On by `dt` seconds, with the eyes where they are now. `orbit`: in a class, the seconds into its
   * ring's arc the class's shared time says (ADR-054, 20 Oct 2026): going round, it is put there
   * rather than moved on by its own frames, so every headset shows it at the same point of its lap;
   * still turning in when the arc has begun, it is turned to the ring and put there too.
   */
  update(dt: number, eye: Ground = this.#eye, orbit?: number): PerformancePose {
    this.#eye = eye;
    if (orbit !== undefined && this.#keep(orbit)) return this.pose;
    let left = Math.max(0, dt);
    // A phase that ends part-way through a frame hands the rest of it on.
    for (let guard = 0; guard < 16 && left >= 0; guard++) {
      const phase = this.#phases[this.#at];
      if (!phase || phase.kind === 'hold') break;
      const used = this.#step(phase, left);
      if (used === undefined) break;
      left -= used;
      this.#at++;
      this.#t = 0;
      this.#enter();
      if (left <= 0) break;
    }
    return this.pose;
  }

  /** Puts it `orbit` seconds into its ring's arc; false when it is not turning in or going round. */
  #keep(orbit: number): boolean {
    let phase = this.#phases[this.#at];
    const next = this.#phases[this.#at + 1];
    if (phase?.kind === 'turn' && next?.kind === 'orbit') {
      // Not yet on the ring as the class is: it turns in on its own frames.
      if (orbit <= 0) return false;
      this.#turn = phase.to;
      this.#at++;
      this.#t = 0;
      this.#enter();
      phase = next;
    }
    if (phase?.kind !== 'orbit') return false;
    this.#t = Math.max(0, orbit);
    if (this.#step(phase, 0) !== undefined) {
      // A number of rounds the class has already finished: on to what it holds after.
      this.#at++;
      this.#t = 0;
      this.#enter();
    }
    return true;
  }

  #plan({ entry, start, charge, circle }: PerformanceInput): void {
    if (entry.circle !== undefined && circle !== undefined && circle.speed !== undefined) {
      this.#phases.push(
        {
          kind: 'turn',
          from: start.turn,
          to: circle.heading,
          seconds: turnSeconds(start.turn, circle.heading),
          gait: circle.gait,
        },
        { kind: 'orbit', circle, speed: circle.speed },
      );
      if (circle.rounds === 'loop') {
        this.#looping = circle;
        return;
      }
      const then = entry.then ?? MODEL_IDLE;
      this.#after = [then];
      this.#phases.push({ kind: 'hold', animation: then, loop: this.#loops(then) });
      return;
    }
    const walkTo = entry.walkTo;
    // A gait it lacks: its own (rijden for a vehicle), as the replay has it -- never left standing.
    const gait: ModelGait = walkingGait(this.#facts, entry.gait);
    const speed = this.#facts.animations[gait]?.speed;
    const clips = [
      ...new Set<ModelAnimation>(
        [start.animation, gait, entry.play, entry.then].filter(
          (clip): clip is ModelAnimation => clip !== undefined,
        ),
      ),
    ];
    let walked = false;
    if (Array.isArray(walkTo) && speed !== undefined && speed > 0) {
      const to: Ground = [walkTo[0], walkTo[1]];
      const length = distance(start.pos, to);
      if (length > 1e-6) {
        const heading = turnTowards(start.pos, to);
        this.#phases.push(
          {
            kind: 'turn',
            from: start.turn,
            to: heading,
            seconds: turnSeconds(start.turn, heading),
            gait,
          },
          { kind: 'walk', from: start.pos, to, heading, gait, speed },
        );
        walked = true;
      }
    } else if (walkTo === 'pupil' && speed !== undefined && speed > 0) {
      const counted = charge?.clips ?? clips;
      const stop =
        charge?.to ??
        modelStopPoint(start.pos, this.#eye, this.#facts, { scale: this.#scale, clips: counted });
      // No room to walk: it stays as it stands, not turned into a bite past the clamp.
      if (distance(start.pos, stop) > ARRIVED) {
        const heading = charge?.heading ?? turnTowards(start.pos, this.#eye);
        this.#phases.push(
          {
            kind: 'turn',
            from: start.turn,
            to: heading,
            seconds: turnSeconds(start.turn, heading),
            gait,
          },
          { kind: 'charge', gait, speed, clips: counted },
        );
        walked = true;
      }
    }
    // A turn in place (plan 4): from where it stands, never after a walk.
    if (!walked && entry.walkTo === undefined && entry.turn !== undefined) {
      const seconds = showTurnSeconds(start.turn, entry.turn);
      if (seconds > 0)
        this.#phases.push({ kind: 'show', from: start.turn, to: entry.turn, seconds });
      else this.#turn = entry.turn;
    }
    const then = entry.then ?? MODEL_IDLE;
    this.#after = [...new Set<ModelAnimation>([...(entry.play ? [entry.play] : []), then])];
    if (entry.face === 'pupil') this.#phases.push({ kind: 'face' });
    const thenLoops = this.#facts.animations[then]?.loop ?? then === MODEL_IDLE;
    if (entry.play !== undefined) {
      const clip = this.#facts.animations[entry.play];
      const loops = entry.loop ?? clip?.loop ?? entry.play === MODEL_IDLE;
      if (loops) this.#phases.push({ kind: 'hold', animation: entry.play, loop: true });
      else
        this.#phases.push(
          { kind: 'play', animation: entry.play, seconds: clip?.seconds, then },
          { kind: 'hold', animation: then, loop: thenLoops },
        );
    } else if (walked || entry.then !== undefined) {
      this.#phases.push({ kind: 'hold', animation: then, loop: thenLoops });
    }
  }

  /** A phase begins: its clip, and what it needs to know of where it starts. */
  #enter(): void {
    const phase = this.#phases[this.#at];
    if (!phase) return;
    switch (phase.kind) {
      case 'turn':
        this.#animation = phase.gait;
        this.#timeScale = MODEL_MOTION.turnGait;
        this.#loop = true;
        return;
      case 'walk':
      case 'charge':
        this.#animation = phase.gait;
        this.#timeScale = MODEL_MOTION.timeScaleMin;
        this.#loop = true;
        return;
      case 'orbit':
        this.#animation = phase.circle.gait;
        this.#timeScale = MODEL_MOTION.timeScaleMin;
        this.#loop = true;
        return;
      case 'show':
        if (isGait(this.#animation)) {
          this.#animation = MODEL_IDLE;
          this.#loop = true;
        }
        this.#timeScale = 1;
        return;
      case 'face': {
        const to = turnTowards(this.#pos, this.#eye);
        // Facing would swing its front past the clamp: it stays turned as it is.
        const near =
          distance(this.#pos, this.#eye) < this.#reach([this.#animation, ...this.#after]);
        this.#face =
          near || Math.abs(shortestTurn(this.#turn, to)) < NO_TURN
            ? undefined
            : { from: this.#turn, to, seconds: faceSeconds(this.#turn, to) };
        if (isGait(this.#animation)) {
          this.#animation = MODEL_IDLE;
          this.#loop = true;
        }
        this.#timeScale = 1;
        return;
      }
      case 'play': {
        const safe = this.#safe(phase.animation, phase.then);
        this.#animation = safe;
        this.#timeScale = 1;
        this.#loop = safe === phase.animation ? false : this.#loops(safe);
        return;
      }
      case 'hold': {
        const safe = this.#safe(phase.animation, MODEL_IDLE);
        this.#animation = safe;
        this.#timeScale = 1;
        this.#loop = safe === phase.animation ? phase.loop : this.#loops(safe);
        return;
      }
    }
  }

  /**
   * `animation`, unless what it sweeps ahead -- a bite's reach -- would come
   * past the clamp from where the model stands and faces now (the pupil came
   * near, or it was already that near): then `instead`, or the idle.
   */
  #safe(animation: ModelAnimation, instead: ModelAnimation): ModelAnimation {
    const clear = (clip: ModelAnimation): boolean => {
      const reach = modelFront(this.#facts, { scale: this.#scale, clips: [clip] });
      const t = (this.#turn * Math.PI) / 180;
      const front: Ground = [
        this.#pos[0] - Math.sin(t) * reach,
        this.#pos[1] - Math.cos(t) * reach,
      ];
      return distance(front, this.#eye) >= modelStopDistance(this.#facts, this.#scale) - 1e-6;
    };
    if (clear(animation)) return animation;
    return instead !== animation && clear(instead) ? instead : MODEL_IDLE;
  }

  #loops(animation: ModelAnimation): boolean {
    return this.#facts.animations[animation]?.loop ?? animation === MODEL_IDLE;
  }

  /** How near its middle may come to the eyes and keep its front at the clamp. */
  #reach(clips: readonly ModelAnimation[] = [this.#animation]): number {
    return (
      modelStopDistance(this.#facts, this.#scale) +
      modelFront(this.#facts, { scale: this.#scale, clips })
    );
  }

  /** Plays `dt` of a phase; the seconds it used when it ended, else undefined. */
  #step(phase: Phase, dt: number): number | undefined {
    const t0 = this.#t;
    this.#t += dt;
    switch (phase.kind) {
      case 'turn': {
        this.#turn = turnAt(this.#t, phase.from, phase.to);
        return this.#t >= phase.seconds ? Math.max(0, phase.seconds - t0) : undefined;
      }
      case 'walk': {
        const length = distance(phase.from, phase.to);
        const total = walkSeconds(length, phase.speed);
        const at = walkAt(Math.min(this.#t, total), length, phase.speed);
        const k = length > 0 ? at.distance / length : 1;
        this.#pos = [
          phase.from[0] + (phase.to[0] - phase.from[0]) * k,
          phase.from[1] + (phase.to[1] - phase.from[1]) * k,
        ];
        this.#turn = phase.heading;
        this.#timeScale = clipTimeScale(at.speed, phase.speed);
        if (this.#t < total) return undefined;
        this.#pos = [phase.to[0], phase.to[1]];
        return Math.max(0, total - t0);
      }
      case 'orbit': {
        const at = circleAt(phase.circle, this.#t);
        this.#pos = [at.pos[0], at.pos[1]];
        this.#turn = at.turn;
        this.#timeScale = clipTimeScale(at.speed, phase.speed);
        if (!at.done) return undefined;
        return Math.max(0, circleArcSeconds(phase.circle) - t0);
      }
      case 'charge':
        return this.#charge(phase, dt);
      case 'show': {
        this.#turn = showTurnAt(this.#t, phase.from, phase.to);
        return this.#t >= phase.seconds ? Math.max(0, phase.seconds - t0) : undefined;
      }
      case 'face': {
        const face = this.#face;
        if (!face) return 0;
        this.#turn = faceAt(this.#t, face.from, face.to);
        return this.#t >= face.seconds ? Math.max(0, face.seconds - t0) : undefined;
      }
      case 'play': {
        const seconds = phase.seconds ?? 0;
        return this.#t >= seconds ? Math.max(0, seconds - t0) : undefined;
      }
      case 'hold':
        return undefined;
    }
  }

  /**
   * A frame of the run at the pupil: towards where the clamp stops its front
   * from the eyes as they are now; never on while there, and arrived when it
   * stands there (or stops after a second on the way).
   */
  #charge(phase: Extract<Phase, { kind: 'charge' }>, dt: number): number | undefined {
    const stop = modelStopPoint(this.#pos, this.#eye, this.#facts, {
      scale: this.#scale,
      clips: phase.clips,
    });
    const left = distance(this.#pos, stop);
    const v = walkSpeed(this.#t, left, phase.speed);
    const moved = Math.min(v * dt, left);
    if (left > 1e-9 && moved > 0) {
      const k = moved / left;
      this.#pos = [
        this.#pos[0] + (stop[0] - this.#pos[0]) * k,
        this.#pos[1] + (stop[1] - this.#pos[1]) * k,
      ];
    }
    // Head first, at the eyes; never faster round than a turn on the spot.
    const toward = turnTowards(this.#pos, this.#eye);
    const most = MODEL_MOTION.turnRate * dt;
    this.#turn += Math.max(-most, Math.min(most, shortestTurn(this.#turn, toward)));
    this.#timeScale = clipTimeScale(v, phase.speed);
    const arrived = left - moved <= ARRIVED || (v < 0.05 && this.#t > 1);
    return arrived ? dt : undefined;
  }
}
